
import {
  Box,
  Button,
  Grid,
  IconButton,
  InputAdornment,
  styled,
  TextField,
  Typography,
  Collapse,
  LinearProgress,
  Autocomplete,
} from "@mui/material";
import axios from "axios";
import colors from "colors/color";
import TextDivider from "components/Deviders/textDevider";
import { useState, useMemo } from "react";
import { GoogleLogin } from "react-google-login";
import { useTranslation } from "react-i18next";
import { motion } from "framer-motion";
import googleLogo from "../../assets/img/google.png";
import linkedinLogo from "../../assets/img/linkedin.png";
import closed_eye_icon from "../../assets/img/closed_eye_icon.svg";
import opened_eye_icon from "../../assets/img/open_eye_icon.svg";
import GlobalCityAutocomplete from "../../components/CitiesPicker/CitiesPicker";

const StyledTextField = styled(TextField)(({ error }) => ({
  "& .MuiOutlinedInput-root": {
    marginTop: 2,
    gap: 10,
    borderRadius: "12px",
    border: error ? "1px solid #d32f2f" : "1px solid #D9D9D9",
    background: "#FFFFFF",
    transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
    "& .MuiOutlinedInput-notchedOutline": {
      borderColor: "transparent",
    },
    "&:hover": {
      borderColor: error ? "#d32f2f" : "#0017E7",
      boxShadow: error ? "0px 0px 4px rgba(211, 47, 47, 0.2)" : "0px 4px 12px rgba(0, 23, 231, 0.08)",
    },
    "&.Mui-focused": {
      borderColor: error ? "#d32f2f" : "#0017E7",
      boxShadow: error ? "0px 0px 8px rgba(211, 47, 47, 0.25)" : "0px 4px 12px rgba(0, 23, 231, 0.15)",
    },
    "& .MuiOutlinedInput-input": {
      minHeight: "36px",
      padding: "10px 14px",
      fontFamily: "Poppins",
      fontSize: "0.95rem",
    },
  },
  "& .MuiFormHelperText-root": {
    marginLeft: 4,
    fontFamily: "Poppins",
    fontSize: "0.75rem",
  }
}));

const PasswordRequirement = ({ met, text }) => (
  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
    <Box
      sx={{
        width: 6,
        height: 6,
        borderRadius: '50%',
        backgroundColor: met ? '#4caf50' : '#e0e0e0',
        transition: 'all 0.3s ease'
      }}
    />
    <Typography
      sx={{
        fontSize: '0.7rem',
        color: met ? '#4caf50' : '#00000066',
        fontFamily: 'Poppins',
        fontWeight: met ? 600 : 400,
        transition: 'all 0.3s ease'
      }}
    >
      {text}
    </Typography>
  </Box>
);

const countries = [
  { label: 'Morocco', code: 'MA' },
  { label: 'France', code: 'FR' },
  { label: 'United States', code: 'US' },
  { label: 'Canada', code: 'CA' },
  { label: 'United Kingdom', code: 'GB' },
  { label: 'Germany', code: 'DE' },
  { label: 'Spain', code: 'ES' },
  { label: 'Italy', code: 'IT' },
  { label: 'Belgium', code: 'BE' },
  { label: 'Switzerland', code: 'CH' },
  { label: 'Tunisia', code: 'TN' },
  { label: 'Algeria', code: 'DZ' },
  { label: 'Senegal', code: 'SN' },
  { label: 'Ivory Coast', code: 'CI' },
].sort((a, b) => a.label.localeCompare(b.label));

const industries = [
  'Technologie de l\'information',
  'Services financiers',
  'Santé et Pharma',
  'Éducation',
  'Industrie et Manufacture',
  'Commerce et Distribution',
  'Tourisme et Hôtellerie',
  'Construction et BTP',
  'Énergie et Environnement',
  'Logistique et Transport',
  'Marketing et Communication',
  'Ressources Humaines',
  'Conseil et Audit',
  'Autre'
].sort();

const CompanySignUp = ({
  apiUrl,
  toggleAuthMode,
  onSignUpSuccess,
  onGoogleSuccess,
  onLinkedInAuth,
  setSnackbar,
  clientId,
}) => {
  const { t } = useTranslation();
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [touched, setTouched] = useState({});

  const [formData, setFormData] = useState({
    email: "",
    password: "",
    password2: "",
    companyName: "",
    responsibleName: "",
    address: "",
    city: "",
    country: "",
    industry: "",
    website: "",
    description: "",
  });

  const handleInputChange = (field) => (e) => {
    setFormData(prev => ({ ...prev, [field]: e.target.value }));
    if (!touched[field]) setTouched(prev => ({ ...prev, [field]: true }));
  };

  const handleBlur = (field) => () => {
    setTouched(prev => ({ ...prev, [field]: true }));
  };

  const validateEmail = (email) => {
    const re = /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
    return re.test(String(email).toLowerCase());
  };

  const passwordValidation = useMemo(() => {
    const p = formData.password;
    return {
      length: p.length >= 8,
      upper: /[A-Z]/.test(p),
      lower: /[a-z]/.test(p),
      number: /[0-9]/.test(p),
      special: /[^a-zA-Z\d\s]/.test(p),
    };
  }, [formData.password]);

  const passwordStrength = useMemo(() => {
    const metCount = Object.values(passwordValidation).filter(Boolean).length;
    return (metCount / 5) * 100;
  }, [passwordValidation]);

  const getFieldError = (field) => {
    if (!touched[field]) return "";
    const value = formData[field]?.trim() || "";

    switch (field) {
      case 'companyName':
        if (!value) return t("registration.errors.company_name_required");
        break;
      case 'responsibleName':
        if (!value) return t("registration.errors.responsible_name_required");
        break;
      case 'email':
        if (!value) return t("registration.errors.email_required");
        if (!validateEmail(value)) return t("registration.errors.invalid_email_format");
        break;
      case 'password':
        if (!value) return t("registration.errors.password_required");
        if (Object.values(passwordValidation).some(v => !v)) return t("registration.errors.password_requirements");
        break;
      case 'password2':
        if (value !== formData.password) return t("registration.errors.passwords_do_not_match");
        break;
      case 'address':
      case 'city':
      case 'country':
      case 'industry':
        if (!value) return t(`registration.errors.${field}_required`);
        break;
      default:
        return "";
    }
  };

  const isFormValid = useMemo(() => {
    const fieldsToVerify = ['companyName', 'responsibleName', 'address', 'city', 'country', 'industry', 'email', 'password', 'password2'];
    return fieldsToVerify.every(field => !getFieldError(field) && formData[field]);
  }, [formData, touched, t]);

  const handleRegistration = async () => {
    // Mark all relevant fields as touched to show errors
    const allFields = Object.keys(formData);
    const newTouched = {};
    allFields.forEach(f => newTouched[f] = true);
    setTouched(newTouched);

    if (!isFormValid) {
      setSnackbar({
        open: true,
        message: t("registration.errors.please_fix_errors"),
        severity: "error",
      });
      return;
    }

    setIsLoading(true);
    try {
      const payload = {
        email: formData.email,
        password: formData.password,
        registration_type: 'company',
        company: formData.companyName,
        address: formData.address,
        city: formData.city,
        country: formData.country,
        comp_industry: formData.industry,
        site_url: formData.website,
        comp_description: formData.description,
      };

      // Split responsible name into first and last name
      const nameParts = formData.responsibleName.trim().split(/\s+/);
      payload.first_name = nameParts[0] || '';
      payload.last_name = nameParts.slice(1).join(' ') || 'Admin';
      payload.username = formData.companyName.replace(/\s+/g, '_').toLowerCase();

      await axios.post(`${apiUrl}authentication/signup/`, payload);

      setSnackbar({
        open: true,
        message: t("registration.success.message"),
        severity: "success",
      });

      onSignUpSuccess();
      toggleAuthMode();
    } catch (err) {
      const errorMsg = err.response?.data?.error || err.response?.data?.email?.[0] || t("registration.errors.unexpected_error");
      setSnackbar({
        open: true,
        message: errorMsg,
        severity: "error",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const renderFieldLabel = (label, required) => (
    <Typography
      sx={{
        color: "#00000099",
        fontFamily: "Poppins",
        fontWeight: 600,
        fontSize: 12,
        mb: 0.5,
        display: 'flex',
        alignItems: 'center'
      }}
    >
      {label}
      {required && <Box component="span" sx={{ color: '#0017E7', ml: 0.5 }}>*</Box>}
    </Typography>
  );

  return (
    <Box sx={{ width: '100%', maxWidth: '100%', mx: 0 }}>

      <Box 
        sx={{ 
          maxHeight: '100%',
          height: '100%',
          overflowY: 'auto',
          px: 1,
          pb: 2,
          '&::-webkit-scrollbar': {
            width: '6px',
          },
          '&::-webkit-scrollbar-track': {
            background: '#f1f1f1',
            borderRadius: '10px',
          },
          '&::-webkit-scrollbar-thumb': {
            background: '#ccc',
            borderRadius: '10px',
          },
          '&::-webkit-scrollbar-thumb:hover': {
            background: '#bbb',
          },
        }}
      >
        <Grid container spacing={2}>
          <Grid item xs={12}>
            {renderFieldLabel(t("registration.company_name"), true)}
            <StyledTextField
              fullWidth
              value={formData.companyName}
              onChange={handleInputChange('companyName')}
              onBlur={handleBlur('companyName')}
              error={!!getFieldError('companyName')}
              helperText={getFieldError('companyName')}
            />
          </Grid>
          <Grid item xs={12}>
            {renderFieldLabel(t("registration.responsible_name"), true)}
            <StyledTextField
              fullWidth
              value={formData.responsibleName}
              onChange={handleInputChange('responsibleName')}
              onBlur={handleBlur('responsibleName')}
              error={!!getFieldError('responsibleName')}
              helperText={getFieldError('responsibleName')}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            {renderFieldLabel(t("registration.company_address"), true)}
            <StyledTextField
              fullWidth
              value={formData.address}
              onChange={handleInputChange('address')}
              onBlur={handleBlur('address')}
              error={!!getFieldError('address')}
              helperText={getFieldError('address')}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            {renderFieldLabel(t("registration.company_city"), true)}
            <GlobalCityAutocomplete
              value={formData.city ? { label: formData.city, value: formData.city } : null}
              onChange={(val) => setFormData(prev => ({ ...prev, city: val || "" }))}
              error={!!getFieldError('city')}
              helperText={getFieldError('city')}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            {renderFieldLabel(t("registration.company_country"), true)}
            <Autocomplete
              options={countries}
              getOptionLabel={(option) => option.label}
              value={countries.find(c => c.label === formData.country) || null}
              onChange={(event, newValue) => {
                setFormData(prev => ({ ...prev, country: newValue ? newValue.label : "" }));
              }}
              renderInput={(params) => (
                <StyledTextField
                  {...params}
                  fullWidth
                  error={!!getFieldError('country')}
                  helperText={getFieldError('country')}
                />
              )}
              sx={{
                '& .MuiAutocomplete-inputRoot': {
                  padding: '0px !important',
                }
              }}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            {renderFieldLabel(t("registration.company_industry"), true)}
            <Autocomplete
              options={industries}
              value={formData.industry || null}
              onChange={(event, newValue) => {
                setFormData(prev => ({ ...prev, industry: newValue || "" }));
              }}
              renderInput={(params) => (
                <StyledTextField
                  {...params}
                  fullWidth
                  error={!!getFieldError('industry')}
                  helperText={getFieldError('industry')}
                />
              )}
              sx={{
                '& .MuiAutocomplete-inputRoot': {
                  padding: '0px !important',
                }
              }}
            />
          </Grid>
          <Grid item xs={12}>
            {renderFieldLabel(t("registration.company_website"), false)}
            <StyledTextField
              fullWidth
              value={formData.website}
              onChange={handleInputChange('website')}
              onBlur={handleBlur('website')}
            />
          </Grid>
          <Grid item xs={12}>
            {renderFieldLabel(t("registration.company_description"), false)}
            <StyledTextField
              fullWidth
              multiline
              rows={3}
              value={formData.description}
              onChange={handleInputChange('description')}
              onBlur={handleBlur('description')}
            />
          </Grid>
        </Grid>

        <Box sx={{ mt: 2 }}>
          {renderFieldLabel(t("registration.enter_email"), true)}
          <StyledTextField
            fullWidth
            type="email"
            value={formData.email}
            onChange={handleInputChange('email')}
            onBlur={handleBlur('email')}
            error={!!getFieldError('email')}
            helperText={getFieldError('email')}
          />
        </Box>

        <Box sx={{ mt: 2 }}>
          {renderFieldLabel(t("registration.enter_password"), true)}
          <StyledTextField
            fullWidth
            type={showPassword ? "text" : "password"}
            value={formData.password}
            onChange={handleInputChange('password')}
            onBlur={handleBlur('password')}
            error={!!getFieldError('password')}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton onClick={() => setShowPassword(!showPassword)} edge="end">
                    <img
                      src={showPassword ? opened_eye_icon : closed_eye_icon}
                      alt="toggle password"
                      style={{ width: 18, height: 18, opacity: 0.6 }}
                    />
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />

          <Collapse in={!!formData.password}>
            <Box sx={{ mt: 1.5, px: 0.5 }}>
              <LinearProgress
                variant="determinate"
                value={passwordStrength}
                sx={{
                  height: 4,
                  borderRadius: 2,
                  mb: 1.5,
                  backgroundColor: '#eee',
                  '& .MuiLinearProgress-bar': {
                    backgroundColor: passwordStrength < 40 ? '#f44336' : passwordStrength < 80 ? '#ff9800' : '#4caf50',
                  }
                }}
              />
              <Grid container spacing={0.5}>
                <Grid item xs={6}>
                  <PasswordRequirement met={passwordValidation.length} text={t("password_requirements.length")} />
                  <PasswordRequirement met={passwordValidation.upper} text={t("password_requirements.uppercase")} />
                  <PasswordRequirement met={passwordValidation.lower} text={t("password_requirements.lowercase")} />
                </Grid>
                <Grid item xs={6}>
                  <PasswordRequirement met={passwordValidation.number} text={t("password_requirements.number")} />
                  <PasswordRequirement met={passwordValidation.special} text={t("password_requirements.special")} />
                </Grid>
              </Grid>
            </Box>
          </Collapse>
        </Box>

        <Box sx={{ mt: 2, mb: 4 }}>
          {renderFieldLabel(t("registration.confirm_password"), true)}
          <StyledTextField
            fullWidth
            type={showPassword ? "text" : "password"}
            value={formData.password2}
            onChange={handleInputChange('password2')}
            onBlur={handleBlur('password2')}
            error={!!getFieldError('password2')}
            helperText={getFieldError('password2')}
          />
        </Box>

        <Box sx={{ mt: 2 }}>
          <Grid container spacing={1}>
            <Grid item xs={12}>
              <Button
                fullWidth
                variant="contained"
                onClick={handleRegistration}
                disabled={isLoading || !isFormValid}
                sx={{
                  mb: 1,
                  p: { xs: 1, md: 1.3 },
                  color: "white",
                  fontFamily: "Poppins",
                  textTransform: "none",
                  fontSize: { xs: "14px", md: "16px" },
                  borderRadius: "15px",
                  gap: 10,
                  background: "#0017E7",
                  fontWeight: 500,
                  "&:hover": {
                    background: "#0012B8",
                  },
                }}
              >
                {isLoading ? (
                  <motion.div
                    animate={{ rotate: 360 }}
                    transition={{ repeat: Infinity, duration: 1, ease: "linear" }}
                    style={{ display: 'flex' }}
                  >
                    ⏳
                  </motion.div>
                ) : t("registration.sign_up")}
              </Button>
            </Grid>
            {/* <Grid item xs={12}>
              <Button
                fullWidth
                variant="outlined"
                onClick={toggleAuthMode}
                sx={{
                  mb: 1,
                  p: { xs: 1, md: 1.3 },
                  color: colors.primary,
                  border: "1px solid #0017E7",
                  fontFamily: "Poppins",
                  textTransform: "none",
                  fontSize: { xs: "14px", md: "14px" },
                  borderRadius: "15px",
                  gap: 10,
                  background: "#fff",
                  fontWeight: 500,
                  "&:hover": {
                    background: "rgba(0, 23, 231, 0.04)",
                    border: "1px solid #0017E7",
                  }
                }}
              >
                {t("login.already_have_account")}
              </Button>
            </Grid> */}
          </Grid>
        </Box>

        {/* <Box sx={{ my: 2 }}>
          <TextDivider>{t("login.or_continue_with")}</TextDivider>
        </Box> */}

        {/* <Grid container spacing={1}>
          <Grid item xs={12}>
            <GoogleLogin
              clientId={clientId}
              onSuccess={onGoogleSuccess}
              onFailure={(res) => {
                console.error("Google failed", res);
                setSnackbar({ open: true, message: t("registration.errors.google_login_failed"), severity: "error" });
              }}
              render={({ onClick }) => (
                <Button
                  fullWidth
                  variant="outlined"
                  onClick={onClick}
                  sx={{
                    mb: 1,
                    p: { xs: 1, md: 1.3 },
                    color: colors.primary,
                    border: "1px solid #0017E7",
                    fontFamily: "Poppins",
                    textTransform: "none",
                    fontSize: { xs: "14px", md: "14px" },
                    borderRadius: "15px",
                    gap: 2,
                    background: "#fff",
                    fontWeight: 500,
                    height: 50,
                    "&:hover": {
                      background: "rgba(0, 23, 231, 0.04)",
                      border: "1px solid #0017E7",
                    }
                  }}
                >
                  <img
                    src={googleLogo}
                    alt="Google Logo"
                    style={{
                      width: "20px",
                      height: "20px",
                    }}
                  />
                  <Typography sx={{ ml: 1 }}>{t("login.sign_in_with_google")}</Typography>
                </Button>
              )}
            />
          </Grid>
          <Grid item xs={12}>
            <Button
              fullWidth
              variant="outlined"
              onClick={onLinkedInAuth}
              sx={{
                mb: 1,
                p: { xs: 1, md: 1.3 },
                color: colors.primary,
                border: "1px solid #0017E7",
                fontFamily: "Poppins",
                textTransform: "none",
                fontSize: "14px",
                borderRadius: "15px",
                gap: 2,
                background: "#fff",
                fontWeight: 500,
                height: 50,
                "&:hover": {
                  background: "rgba(0, 23, 231, 0.04)",
                  border: "1px solid #0017E7",
                }
              }}
            >
              <img
                src={linkedinLogo}
                alt="LinkedIn Logo"
                style={{
                  width: "20px",
                  height: "20px",
                }}
              />
              <Typography sx={{ ml: 1 }}>{t("login.sign_in_with_linkedin")}</Typography>
            </Button>
          </Grid>
        </Grid> */}
      </Box>
    </Box>
  );
};

export default CompanySignUp;
